import type { RefObject } from "preact";
import { useLayoutEffect, useRef } from "preact/hooks";

/** 按下到抬起挪了这么远就是在拖页面，不算点。 */
const TAP_SLOP_PX = 10;
/** 点到链接、按钮后最多等它的 click 这么久，等不到也收起。 */
const CLICK_WAIT_MS = 400;
const CLICKABLE =
  'a[href], button, input, select, textarea, label, summary, [role="button"], [role="link"], [onclick]';

/**
 * 抽屉开着时，点一下挂件外露出的店面（GON-180）。只认点、不拦点：点到的链接和按钮照常响应。
 * 用 pointer 事件而不是 click：iOS 点没有点击处理的普通文字时，click 不一定派发到 document。
 * 浏览器接手滚动会发 pointercancel，拖页面因此不算点。
 */
export function useOutsideTap(
  host: RefObject<HTMLElement>,
  enabled: boolean,
  onTap: () => void,
) {
  const tap = useRef(onTap);
  tap.current = onTap;
  useLayoutEffect(() => {
    const container = host.current;
    if (!enabled || !container) return;
    const doc = container.ownerDocument;
    let press:
      { id: number; x: number; y: number; clickable: boolean } | undefined;
    let waiting: (() => void) | undefined;
    const down = (event: PointerEvent) => {
      const path = event.composedPath();
      const origin = path[0];
      // 第二根手指按下（双指缩放）也会走到这里，把前一根的点作废。
      press =
        event.isPrimary && event.button === 0 && !path.includes(container)
          ? {
              id: event.pointerId,
              x: event.clientX,
              y: event.clientY,
              clickable:
                path.some(
                  (node) => node instanceof Element && node.matches(CLICKABLE),
                ) ||
                (origin instanceof Element &&
                  getComputedStyle(origin).cursor === "pointer"),
            }
          : undefined;
    };
    const cancel = () => {
      press = undefined;
    };
    const up = (event: PointerEvent) => {
      if (!press || event.pointerId !== press.id) return;
      const { clickable } = press;
      const moved = Math.hypot(
        event.clientX - press.x,
        event.clientY - press.y,
      );
      press = undefined;
      if (moved > TAP_SLOP_PX) return;
      if (!clickable) {
        tap.current();
        return;
      }
      // 别在这里直接收：收起时 iOS 店面从 body 滚动换回整页滚动，随后派发 click 时按旧坐标重新命中，
      // 对不上原来的元素，链接就不跳了。等 click 派发时在捕获阶段同步收，换页前就把关闭存下。
      waiting?.();
      const settle = () => {
        waiting?.();
        tap.current();
      };
      const timer = setTimeout(settle, CLICK_WAIT_MS);
      doc.addEventListener("click", settle, true);
      waiting = () => {
        clearTimeout(timer);
        doc.removeEventListener("click", settle, true);
        waiting = undefined;
      };
    };
    doc.addEventListener("pointerdown", down, true);
    doc.addEventListener("pointerup", up, true);
    doc.addEventListener("pointercancel", cancel, true);
    return () => {
      waiting?.();
      doc.removeEventListener("pointerdown", down, true);
      doc.removeEventListener("pointerup", up, true);
      doc.removeEventListener("pointercancel", cancel, true);
    };
  }, [host, enabled]);
}
